import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Briefcase, Megaphone, Users, ArrowRight, Hourglass, Target, Percent, Building2, LayoutGrid } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useLanguage } from '@/contexts/LanguageContext';
import { ScoreBadge, StatusBadge, ChannelBadge, InterviewSubstatusBadges } from '@/components/shared/Badges';
import { ResponsiveTable, TABLE_COL, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { statusCellClass } from '@/components/ui/responsive-table/columnLayout';
import type { Application } from '@/types';
import { useDashboardMetrics, type Period } from '@/hooks/useDashboardMetrics';
import { HeroMetricCard } from '@/components/dashboard/HeroMetricCard';
import { FunnelChart } from '@/components/dashboard/FunnelChart';
import { ChannelChart } from '@/components/dashboard/ChannelChart';
import { TrendChart } from '@/components/dashboard/TrendChart';
import { TimeInStateChart } from '@/components/dashboard/TimeInStateChart';
import { TopPositionsTable } from '@/components/dashboard/TopPositionsTable';
import { CapUsageWidgets } from '@/components/dashboard/CapUsageWidgets';
import { AgencyHeaderCard } from '@/components/dashboard/AgencyHeaderCard';
import { AgencyPlanDashboard } from '@/components/dashboard/AgencyPlanDashboard';
import { DashboardPeriodSelector } from '@/components/dashboard/DashboardPeriodSelector';
import { PipelineStatusGrid } from '@/components/dashboard/PipelineStatusGrid';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { canProcessPendingApplications } from '@/lib/pendingProcessing';
import { isApplicationArchived } from '@/lib/applicationArchive';

const DashboardPage = () => {
  const { selectedCompany, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { positions, postings, applications, loading } = useDashboardData(selectedCompany?.id);
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const [period, setPeriod] = useState<Period>('30d');
  const [activeTab, setActiveTab] = useState<'company' | 'plan'>('company');

  const isAgencyOwner = user?.accountType === 'agency_owner' && !!user.agency;
  const companyName = selectedCompany?.name ?? t('dashboard.tab_company');

  const periodOptions: { value: Period; label: string }[] = [
    { value: '7d', label: t('dashboard.7days') },
    { value: '30d', label: t('dashboard.30days') },
    { value: '90d', label: t('dashboard.90days') },
    { value: 'all', label: t('dashboard.allTime') },
  ];

  const m = useDashboardMetrics(applications, positions, postings, period, {
    hasProcessingCredits,
  });

  // Sprint dashboard_alignment_v1 (26/05/26 22:00): recent apps now respects
  // the period selector — recruiters were confused seeing 30d-old apps on
  // the "ultime 30 giorni" view.
  const recentApps = useMemo(() => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : null;
    let pool = applications.filter((a) => !isApplicationArchived(a));
    if (days !== null) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      cutoff.setHours(0, 0, 0, 0);
      pool = pool.filter(a => new Date(a.appliedAt) >= cutoff);
    }
    return [...pool]
      .sort((a, b) => new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime())
      .slice(0, 10);
  }, [applications, period]);

  const recentAppsColumns: ResponsiveTableColumn<Application>[] = [
    {
      id: 'candidate',
      label: t('dashboard.candidate'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.title,
      truncate: true,
      render: (row) => (
        <span className="font-medium truncate block" title={`${row.candidateFirstName} ${row.candidateLastName}`}>
          {row.candidateFirstName} {row.candidateLastName}
        </span>
      ),
    },
    {
      id: 'position',
      label: t('dashboard.position'),
      priority: 'secondary',
      ...TABLE_COL.text,
      truncate: true,
      render: (row) => (
        <span className="text-muted-foreground truncate block" title={row.positionTitle}>{row.positionTitle}</span>
      ),
    },
    {
      id: 'source',
      label: t('dashboard.source'),
      priority: 'tertiary',
      ...TABLE_COL.channel,
      render: (row) => <ChannelBadge channel={row.channel} />,
    },
    {
      id: 'score',
      label: t('dashboard.score'),
      priority: 'tertiary',
      ...TABLE_COL.score,
      render: (row) => <ScoreBadge score={row.aiScore} />,
    },
    {
      id: 'interview',
      label: t('applications.interview'),
      priority: 'tertiary',
      hideOn: 'tablet',
      ...TABLE_COL.interview,
      defaultVisible: false,
      render: (row) => <InterviewSubstatusBadges interviews={row.interviews} />,
    },
    {
      id: 'status',
      label: t('dashboard.status'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.status,
      render: (row) => (
        <div className={statusCellClass()}>
          <StatusBadge state={row.state} />
        </div>
      ),
    },
  ];

  const CompanyDashboardContent = ({ showPeriodSelector = true }: { showPeriodSelector?: boolean }) => (
    <>
      {showPeriodSelector && (
        <DashboardPeriodSelector
          period={period}
          onPeriodChange={setPeriod}
          options={periodOptions}
        />
      )}

      {/* Hero KPIs — volume & outcomes; pipeline detail lives in PipelineStatusGrid below. */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <HeroMetricCard
          label={t('dashboard.openPositions')}
          value={m.openPositions}
          icon={Briefcase}
          color="text-primary"
          badge={m.agingPositions}
          badgeColor="bg-warning/10 text-warning"
          tooltip={t('dashboard.tooltip.openPositions', { count: m.openPositions, aging: m.agingPositions })}
          onClick={() => navigate('/job-positions?status=active')}
        />
        <HeroMetricCard
          label={t('dashboard.activePostings')}
          value={m.activePostings}
          icon={Megaphone}
          color="text-success"
          tooltip={t('dashboard.tooltip.activePostings')}
        />
        <HeroMetricCard
          label={t('dashboard.applications')}
          value={m.periodAppCount}
          icon={Users}
          color="text-muted-foreground"
          change={m.appChange}
          tooltip={t('dashboard.tooltip.applications', { total: applications.length })}
          onClick={() => navigate('/applications')}
        />
        <HeroMetricCard
          label={t('dashboard.offerAcceptance')}
          value={m.oar !== null ? `${(m.oar * 100).toFixed(0)}%` : '—'}
          icon={Percent}
          color="text-success"
          tooltip={t('dashboard.tooltip.offerAcceptance')}
        />
        <HeroMetricCard
          label={t('dashboard.medianTimeToHire')}
          value={m.medianTTH !== null ? `${m.medianTTH.toFixed(1)}d` : '—'}
          icon={Hourglass}
          color="text-primary"
          semaphore={m.tthSemaphore}
          tooltip={t('dashboard.tooltip.medianTimeToHire')}
        />
        <HeroMetricCard
          label={t('dashboard.medianTimeToFill')}
          value={m.medianTTF !== null ? `${m.medianTTF.toFixed(1)}d` : '—'}
          icon={Target}
          color="text-primary"
          semaphore={m.ttfSemaphore}
          tooltip={t('dashboard.tooltip.medianTimeToFill')}
        />
      </div>

      <PipelineStatusGrid key={period} counts={m.pipelineStatus} />

      {/* Company-specific usage caps */}
      <CapUsageWidgets showPlanSection={false} />

      {/* Funnel + Channel */}
      <div className="grid lg:grid-cols-2 gap-6 min-w-0">
        <FunnelChart data={m.funnelWithRates} outcomes={m.funnelOutcomes} className="h-full" />
        <ChannelChart data={m.channelData} className="h-full" />
      </div>

      {/* Conversion KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-border/50"><CardContent className="pt-3 pb-2.5 px-4"><p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.applicantToInterview')}</p><p className="text-base font-semibold">{m.applicantToInterview !== null ? `${m.applicantToInterview.toFixed(1)}%` : '—'}</p></CardContent></Card>
        <Card className="border-border/50"><CardContent className="pt-3 pb-2.5 px-4"><p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.interviewToHire')}</p><p className="text-base font-semibold">{m.interviewToHire !== null ? `${m.interviewToHire.toFixed(1)}%` : '—'}</p></CardContent></Card>
        <Card className="border-border/50"><CardContent className="pt-3 pb-2.5 px-4"><p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.applicantToHire')}</p><p className="text-base font-semibold text-success">{m.applicantToHire !== null ? `${m.applicantToHire.toFixed(1)}%` : '—'}</p></CardContent></Card>
        <Card className="border-border/50"><CardContent className="pt-3 pb-2.5 px-4"><p className="text-[11px] text-muted-foreground mb-0.5">{t('dashboard.applicantToReject')}</p><p className="text-base font-semibold text-destructive">{m.applicantToReject !== null ? `${m.applicantToReject.toFixed(1)}%` : '—'}</p></CardContent></Card>
      </div>

      {/* Positions + Time in State */}
      <div className="grid lg:grid-cols-2 gap-6">
        <TopPositionsTable data={m.topPositions} pipelineErrors={m.pipelineErrors} />
        <TimeInStateChart data={m.timeInStates} />
      </div>

      <TrendChart data={m.trendData} />

      {/* Recent Applications */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm font-medium">{t('dashboard.recentApplications')}</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1" onClick={() => navigate('/applications')}>
              {t('dashboard.viewAll')} <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-0">
          <ResponsiveTable
            data={recentApps}
            columns={recentAppsColumns}
            getRowKey={(app) => app.id}
            onRowClick={(app) =>
              navigate(
                app.positionId
                  ? `/job-positions/${app.positionId}/applications/${app.id}`
                  : `/applications/${app.id}`,
              )
            }
            storageKey="dashboard-recent-apps"
            emptyState={<div className="py-8 text-center text-muted-foreground text-sm">{t('dashboard.noRecent')}</div>}
            ariaLabel={t('dashboard.recentApplications')}
          />
        </CardContent>
      </Card>
    </>
  );

  return (
    <div className="space-y-6 min-w-0">
      {/* Phase 2 Agency — owner header (no-op for individuals) */}
      <AgencyHeaderCard />

      {/* Agency owners see tabs to switch between company and plan views */}
      {isAgencyOwner ? (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'company' | 'plan')} className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
            <TabsList className="grid w-full sm:w-auto sm:max-w-md grid-cols-2 shrink-0">
              <TabsTrigger value="company" className="gap-2">
                <Building2 className="h-4 w-4 shrink-0" />
                <span className="truncate">{companyName}</span>
              </TabsTrigger>
              <TabsTrigger value="plan" className="gap-2">
                <LayoutGrid className="h-4 w-4 shrink-0" />
                {t('dashboard.tab_plan')}
              </TabsTrigger>
            </TabsList>
            <DashboardPeriodSelector
              period={period}
              onPeriodChange={setPeriod}
              options={periodOptions}
              className="py-0 my-0 shrink-0"
            />
          </div>

          <TabsContent value="company" className="space-y-6 mt-0">
            <CompanyDashboardContent showPeriodSelector={false} />
          </TabsContent>

          <TabsContent value="plan" className="space-y-6 mt-0">
            <AgencyPlanDashboard period={period} />
          </TabsContent>
        </Tabs>
      ) : (
        /* SME users see the standard dashboard without tabs */
        <CompanyDashboardContent />
      )}
    </div>
  );
};

export default DashboardPage;
